import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import PropTypes from "prop-types";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import { get, size } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Tabs from "@material-ui/core/Tabs";
import Dialog from "@material-ui/core/Dialog";

import Loader from "../../Common/Loader";
import CloseIcon from "../../Common/CloseIcon";
import CustomTab from "../../Common/CustomTab";
import CustomInput from "../../Common/CustomInput";
import CustomButton from "../../Common/CustomButton";
import ProfileMatch from "./ProfileMatch";
import ProfileForm from "../../Profiles/ProfileForm/ProfileForm";

import { fetchPost, fetchPut, fetchGet } from "../../../api/fetch";

import { updateOneLog } from "../../../actions/siteEntranceExit/tabs";
import { clearStore } from "../../../actions/siteEntranceExit/correctConfirmValue";
import { updateLiveEvents } from "../../../actions/siteEntranceExit/liveEvents";
import { getLov } from "../../../utils/lov";
import { isUnknownProfile } from "../../../utils/profile";
import { getOcrLprLatestValue } from "../../../utils/detectionDetails";

import { CONTEXT } from "../../../constants/common";
import { ATTRIBUTE }from "../../../constants/attributes";

import images from "../../../assets/img/images";

import colors from "../../../assets/style/colors";
import { driveInOut } from "../../../constants/reducers";

const steps = {
  OVERRIDE_CONFIRMATION: "OVERRIDE_CONFIRMATION",
  CREATION_WARNING: "CREATION_WARNING",
  CREATE_PROFILE: "CREATE_PROFILE",
};

const styles = {
  rootDialog: {
    //zIndex: "8200 !important",
  },
  profileCreationWarningDialog: {
    //zIndex: "8100 !important",
  },
  createProfileDialog: {
    height: "950px",
    minWidth: "900px",
  },
  tabRoot: {
    textTransform: "initial",
    fontSize: "1em",
    fontWeight: "400",
    color: colors.black54,
    minWidth: "100px",
    opacity: "1.0",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "10px",
  },
  tabIndicator: {
    display: "none",
  },
  profileBox: {
    width: "400px",
    maxHeight: "120px",
  },
};

const Override = (props) => {
  const {
    classes,
    dispatch,
    section,
    close,
    photos,
    dacId,
    tabs,
    refresh,
    siteId,
    zoneId,
    detectionEventGroup,
    overrideData,
    setOverrideData,
    isLive,
    liveEventCardType,
    liveZoneId,
    siteEntranceExit,
  } = props;
  const { currentProfile, newProfile } = overrideData;
  const profileType = get(currentProfile, ["type", "id"]) || props.profileType;
  const isMounted = React.useRef(true);
  const [step, setStep] = React.useState();
  const [loading, setLoading] = React.useState(false);
  const [reasonOverride, setReasonOverride] = React.useState("");
  const [lov, setLov] = React.useState({});

  React.useEffect(() => () => (isMounted.current = false), []);

  React.useEffect(() => {
    let ac = new AbortController();

    const fetchLov = async () => {
      let ocrTypeLov = await getLov(ATTRIBUTE.OCR_TYPE);
      if (!ac.signal.aborted) {
        setLov({
          135: ocrTypeLov,
        });
      }
    };
    fetchLov();

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (newProfile === "create") {
      setStep(steps.CREATION_WARNING);
    } else {
      setStep(steps.OVERRIDE_CONFIRMATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProfile, newProfile]);

  // Debug useEffect
  // React.useEffect(() => {
  //   console.log("Override - detectionEventGroup:", detectionEventGroup);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [detectionEventGroup])

  const submitOverride = async (createdProfile = null) => {
    let overrideProfile = newProfile;
    if ((!overrideProfile || overrideProfile === "create") && createdProfile) {
      overrideProfile = createdProfile;
      setOverrideData(
        update(overrideData, { newProfile: { $set: createdProfile } })
      );
    }
  
    if (overrideProfile) {
      let body = {
        event_group: {
          id: detectionEventGroup.id || null,
          zone_id: zoneId,
          site_id: siteId,
          update_location: false,
          event: {
            profile: {
              id: overrideProfile.id,
              data: overrideProfile,
            },
          },
        },
      };
      try {
        await fetchPut("override/event_groups/" + detectionEventGroup.parent?.id, body);
        if (reasonOverride && detectionEventGroup.id) {
          await fetchPost("event_groups/" + detectionEventGroup.id + "/notes", {
            description: reasonOverride,
            profile_id: !isUnknownProfile(overrideProfile.id)
              ? overrideProfile.id
              : null,
          });
        }
        if(liveEventCardType) {
          let profile = await fetchGet(`profiles/${overrideProfile.id}`);
          let newState = get(siteEntranceExit, [
            driveInOut,
            "liveEvents",
            liveZoneId
          ]);
          const modifiedState = {
            ...newState,
            [liveEventCardType]:{
              ...newState[liveEventCardType],
              detectionEventGroup: {
                ...newState[liveEventCardType].detectionEventGroup,
                profile: {
                  ...newState[liveEventCardType].detectionEventGroup.profile,
                  ...profile
                }
              }
            }
          }
            
          dispatch(updateLiveEvents(driveInOut, liveZoneId, modifiedState));
        }
        if (refresh) {
          refresh();
        } else if (tabs) {
          dispatch(
            updateOneLog(
              section,
              get(tabs, ["tabIndex"]),
              get(tabs, ["openFrom"]),
              dacId
            )
          );
        }
        if (newProfile !== "create") {
          handleClose();
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      // Profile creation
      setStep(steps.CREATION_WARNING);
    }
  };

  const handleProfileCreation = async (p) => {
    setLoading(true);
    try {
      // Override current profile
      await submitOverride(p);
      if (isMounted.current) setLoading(false);
    } catch (e) {
      console.log(e);
    }
  };

  const handleClose = () => {
    dispatch(clearStore(section));
    close();
  };

  const renderOverrideConfirmation = () => {
    if (
      currentProfile &&
      (!get(currentProfile, ["images"]) ||
        size(get(currentProfile, ["images"])) === 0)
    ) {
      currentProfile.images = photos.filter((p) => p.type === CONTEXT);
    }
    return (
      <>
        <Grid
          item
          className="text-87 bold-500 text-center"
          style={{ marginTop: "20px" }}
        >
          {I18n.get("site_overrideMsg") + ":"}
        </Grid>
        <ProfileMatch
          profile={currentProfile}
          lov={lov}
          searchValue={""}
          className={classes.profileBox}
        />
        <Grid
          item
          className="text-center bold-500"
          style={{ marginTop: "30px" }}
        >
          {I18n.get("site_with")}
        </Grid>
        {newProfile !== "create" ? (
          <ProfileMatch
            profile={newProfile}
            lov={lov}
            searchValue={""}
            className={classes.profileBox}
          />
        ) : (
          <Grid item style={{ width: "400px" }}>
            <span className="text-54">{I18n.get("site_noProfileMsg")}</span>
          </Grid>
        )}
        <Grid item style={{ marginTop: "30px", width: "400px" }}>
          <Box className="text-87 bold-500">
            {I18n.get("site_reasonForOverride")}
          </Box>
          <CustomInput
            name="overrideReason"
            value={reasonOverride || ""}
            onChange={(e) => setReasonOverride(e.target.value)}
            multiline={true}
            height="120px"
          />
        </Grid>
        <Grid item style={{ marginTop: "30px" }}>
          <Grid container alignItems="center" justify="center">
            <Grid item>
              <CustomButton
                style={{ marginRight: "10px" }}
                color="primary"
                startIcon={<img src={images.backArrowColored} alt="back" />}
                onClick={() => setOverrideData(null)}
              >
                {I18n.get("site_back")}
              </CustomButton>
            </Grid>
            <Grid item>
              <CustomButton
                color="primary"
                variant="contained"
                style={{ margin: "0px 0px 0px 10px" }}
                onClick={submitOverride}
              >
                {I18n.get("site_confirm")}
              </CustomButton>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  const renderProfileCreationWarning = () => {
    return (
      <Dialog
        open={true}
        classes={{ root: classes.profileCreationWarningDialog }}
      >
        <Grid container direction="column" style={{ padding: "30px" }}>
          <Grid item className="text-center text-87">
            {I18n.get("site_closePhotoWindowWarning1")}
          </Grid>
          <Grid item className="text-center text-87">
            {I18n.get("site_closePhotoWindowWarning2")}
          </Grid>
          <Grid item style={{ marginTop: "25px" }}>
            <Grid container justify="center">
              <Grid item>
                <CustomButton
                  color="primary"
                  style={{ marginRight: "10px" }}
                  onClick={() => setOverrideData(null)}
                >
                  {I18n.get("site_no")}
                </CustomButton>
              </Grid>
              <Grid item>
                <CustomButton
                  color="primary"
                  style={{ marginLeft: "10px" }}
                  onClick={() => setStep(steps.CREATE_PROFILE)}
                >
                  {I18n.get("site_yes")}
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
    );
  };

  const renderProfileCreation = () => {
    let initialAttributes = getOcrLprLatestValue(photos).filter(
      (v) => v.value !== ""
    );
    const lprAttr = initialAttributes.filter((v) => v.id === 44);
    const ocrAttr = initialAttributes.filter((v) => v.id === 50);
    initialAttributes = [...ocrAttr];
    if (lprAttr && lprAttr.length > 0) {
      initialAttributes.push(lprAttr[0]);
    }

    return (
      <Dialog
        open={true}
        maxWidth="xl"
        classes={{
          root: classes.rootDialog,
          paperWidthXl: classes.createProfileDialog,
        }}
      >
        {loading ? (
          <Loader
            noDecoration
            message={I18n.get("site_creatingProfile")}
            height="100%"
          />
        ) : (
          <ProfileForm
            profileType={profileType}
            profile={{
              profile_type_id: profileType,
              attributes: initialAttributes,
            }}
            setCreatedProfile={(p) => handleProfileCreation(p)}
            close={() => setOverrideData(null)}
            photos={photos
              .filter((p) => p.type && p.type.includes(CONTEXT))
              .map((p) => p.url)}
            detectionId={detectionEventGroup ? detectionEventGroup.id : null}
            profileTypeDetails={get(currentProfile, ["type"])}
          />
        )}
      </Dialog>
    );
  };

  if (step === steps.CREATION_WARNING) {
    return renderProfileCreationWarning();
  } else if (step === steps.CREATE_PROFILE) {
    return renderProfileCreation();
  }

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: "100%" }}>
      <Grid item>
        <Grid container wrap="nowrap" alignItems="center">
          <Grid item xs={2}></Grid>
          <Grid item xs>
            <Tabs
              value={0}
              centered
              classes={{ indicator: classes.tabIndicator }}
            >
              <CustomTab
                height="40px"
                label={I18n.get("site_override")}
                rootClass={classes.tabRoot}
              />
            </Tabs>
          </Grid>
          <Grid item xs={2} className={classes.closeBtn}>
            <CloseIcon
              closeFunction={handleClose}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs
        style={{ maxHeight: "100%", overflow: "auto", paddingBottom: "24px" }}
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          wrap="nowrap"
          style={{ height: "100%" }}
        >
          {step === steps.OVERRIDE_CONFIRMATION ? (
            renderOverrideConfirmation()
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

Override.propTypes = {
  section: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  overrideData: PropTypes.object.isRequired,
  photos: PropTypes.array.isRequired,
  dacId: PropTypes.number.isRequired,
  tabs: PropTypes.object,
  detectionEventGroup: PropTypes.object,
  setOverrideData: PropTypes.func.isRequired,
  liveZoneId: PropTypes.number
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
    siteEntranceExit: state.siteEntranceExit,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(Override));
